import {
  Box, Grid, MenuItem, Select, Typography, Button, FormControl, FormLabel, Radio, CircularProgress,
  RadioGroup, FormControlLabel, InputAdornment, Autocomplete, TextField, Switch, Chip, Alert,
  Tab, Tabs, Paper
} from "@mui/material";
import AttendanceOvertimeRule from "./AttendanceOvertimeRule";
import {Controller, useForm} from 'react-hook-form';
import {useEffect, useState, useRef, useCallback} from "react";
import React from "react";
import {useTranslation} from "react-i18next";
import {useSearchParams} from "react-router-dom";
import NGInputLabel from "@/components/ngteco-form/NGInputLabel";
import {zodResolver} from "@hookform/resolvers/zod";
import {any, object, TypeOf, z} from "zod";
import CustomBreadcrumbs from "@/components/breadcrumbs/CustomBreadcrumbs";
import {ReactComponent as IconSubscriptionPhone} from "@/assets/icons/subscriptionPhone.svg";
import {ReactComponent as SubscriptionAppStore} from "@/assets/icons/subscriptionAppStore.svg";
import GenericService from "@/services/GenericService";
import {ATTEndpoints} from "@/services/att/endpoints";
import {SaveButton} from "@/components/data-grid/CustomButton";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import Util from "@/utils/Util";
import {useSnackbar} from "notistack";
import SnackbarUtil from "@/utils/SnackbarUtil";
import CompanyService from "@/services/auth/CompanyService";
import ControlledTextFiled from "@/components/ngteco-hook-form/ControlledTextFiled";

interface DayOption {
  key: string;
  name: string;
}

// Tab 面板组件
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`attendance-tabpanel-${index}`}
      aria-labelledby={`attendance-tab-${index}`}
      {...other}
      style={{width: '100%'}}
    >
      {value === index && (
        <Box sx={{pt: 3}}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `attendance-tab-${index}`,
    'aria-controls': `attendance-tabpanel-${index}`,
  };
}

const AttendanceManagement: React.FC = () => {
  const {t} = useTranslation();
  const [formData, setFormData] = useState({
    hours_format: '',
    clock_time_format: ''
  });
  // 加班规则状态
  const [overtimeEnabled, setOvertimeEnabled] = useState(false);
  const [overtimeType, setOvertimeType] = useState<'daily' | 'weekly'>('daily');
  const [t1Value, setT1Value] = useState<string>('40');
  const [t2Value, setT2Value] = useState<string>('60');
  const [weeklyAlertsEnabled, setWeeklyAlertsEnabled] = useState(false);
  const [hoursRangeLowMin, setHoursRangeLowMin] = useState<string>('0');
  const [hoursRangeLowMax, setHoursRangeLowMax] = useState<string>('40');
  const [hoursRangeMediumMin, setHoursRangeMediumMin] = useState<string>('40');
  const [hoursRangeMediumMax, setHoursRangeMediumMax] = useState<string>('80');
  const [hoursRangeHighMin, setHoursRangeHighMin] = useState<string>('80');
  const [hoursRangeHighMax, setHoursRangeHighMax] = useState<string>('');
  const [overtimeData, setOvertimeData] = useState<any>(null);

  const DateAddValidate = object({
    effectStartDate:
      z.date(
        {
          errorMap: (issue, {defaultError}) =>
            ({message: issue.code === "invalid_date" ? t("common.Invalid date format.") : issue.code === "invalid_type" ? t("common.This field is required!") : defaultError})
        }),
    share: z.string().optional(),
  })

  const {enqueueSnackbar} = useSnackbar();
  const [subscriptionFlag, setSubscriptionFlag] = useState(false);
  const [period, setPeriod] = useState('weekly');

  // ====================== TAB 状态（预留后续扩展） ======================
  const [tabValue, setTabValue] = useState(0);
  const [searchParams] = useSearchParams();
  
  // Read tab parameter from URL on mount
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'overtime') {
      setTabValue(1); // Overtime Rule tab is at index 1
    }
  }, [searchParams]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  function getWeekDayOption(dateString: string): DayOption {
    const date = new Date(dateString);
    const jsDay = date.getDay();
    let customKeyIndex: number;
    if (jsDay === 0) {
      customKeyIndex = 6; // Sunday
    } else {
      customKeyIndex = jsDay - 1; // Mon-Sat
    }
    const selectedDay = weekDay.find(day => day.key === String(customKeyIndex));

    if (!selectedDay) {
      throw new Error("Invalid day mapping");
    }

    return selectedDay;
  }

  const weekDay: DayOption[] = React.useMemo(() => [
    {key: "0", name: t("att.timeSheet.Monday")},
    {key: "1", name: t("att.timeSheet.Tuesday")},
    {key: "2", name: t("att.timeSheet.Wednesday")},
    {key: "3", name: t("att.timeSheet.Thursday")},
    {key: "4", name: t("att.timeSheet.Friday")},
    {key: "5", name: t("att.timeSheet.Saturday")},
    {key: "6", name: t("att.timeSheet.Sunday")},
  ], [t]);
  const [weekStartDay, setWeekStartDay] = useState<DayOption>(weekDay[0]);
  const [startDay, setStartDay] = useState<string>('0');
  const [dateFormat, setDateFormat] = useState<string>("MM/dd/yyyy");
  const payPeriodDay: DayOption[] = [{key: "1", name: t("att.timeSheet.Day") + " 1"},
    {key: "2", name: t("att.timeSheet.Day") + " 2"}, {key: "3", name: t("att.timeSheet.Day") + " 3"},
    {key: "4", name: t("att.timeSheet.Day") + " 4"}, {key: "5", name: t("att.timeSheet.Day") + " 5"},
    {key: "6", name: t("att.timeSheet.Day") + " 6"}, {key: "7", name: t("att.timeSheet.Day") + " 7"},
    {key: "8", name: t("att.timeSheet.Day") + " 8"}, {key: "9", name: t("att.timeSheet.Day") + " 9"},
    {key: "10", name: t("att.timeSheet.Day") + " 10"}, {key: "11", name: t("att.timeSheet.Day") + " 11"},
    {key: "12", name: t("att.timeSheet.Day") + " 12"}, {key: "13", name: t("att.timeSheet.Day") + " 13"},
    {key: "14", name: t("att.timeSheet.Day") + " 14"}, {key: "15", name: t("att.timeSheet.Day") + " 15"},
    {key: "16", name: t("att.timeSheet.Day") + " 16"}, {key: "17", name: t("att.timeSheet.Day") + " 17"},
    {key: "18", name: t("att.timeSheet.Day") + " 18"}, {key: "19", name: t("att.timeSheet.Day") + " 19"},
    {key: "20", name: t("att.timeSheet.Day") + " 20"}, {key: "21", name: t("att.timeSheet.Day") + " 21"},
    {key: "22", name: t("att.timeSheet.Day") + " 22"}, {key: "23", name: t("att.timeSheet.Day") + " 23"},
    {key: "24", name: t("att.timeSheet.Day") + " 24"}, {key: "25", name: t("att.timeSheet.Day") + " 25"},
    {key: "26", name: t("att.timeSheet.Day") + " 26"}, {key: "27", name: t("att.timeSheet.Day") + " 27"},
    {key: "28", name: t("att.timeSheet.Day") + " 28"}, {key: "29", name: t("att.timeSheet.Day") + " 29"},
    {key: "30", name: t("att.timeSheet.Day") + " 30"}, {key: "31", name: t("att.timeSheet.Day") + " 31"}]

  const [payPeriodDay1, setPayPeriodDay1] = useState<DayOption>(payPeriodDay[0]);
  const [payPeriodDay2, setPayPeriodDay2] = useState<DayOption>(payPeriodDay[14]);
  const reportTypes: DayOption[] = [{key: "0", name: t("common.Basic Report Export")}, {
    key: "1",
    name: t("common.Advanced Report Export")
  }]
  const [reportType, setReportTypes] = useState<DayOption>(reportTypes[0]);
  const [autoReportDelivery, setAutoReportDelivery] = useState<boolean>(true);

  type DateAddForm = TypeOf<typeof DateAddValidate>;
  const dateAddForm = useForm<DateAddForm>({
    resolver: zodResolver(DateAddValidate),
    defaultValues: {}
  })
  useEffect(() => {
  // 更新周起始日
  const weekMatch = weekDay.find(item => item.key === weekStartDay.key);
  if(weekMatch) setWeekStartDay(weekMatch);

}, [t, weekDay]);

  useEffect(() => {
    const fetchSettings = async () => {
      GenericService.list(ATTEndpoints.ATT_ATTENDANCE_SETTING_URI, {current: 0, pageSize: 100}).then((res) => {
        if (res.data && res.status === 200) {
          const hours_format = res.data.hours_format
          const clock_time_format = res.data.clock_time_format
          const pay_period = res.data.pay_period
          setSubscriptionFlag(res.data.subscription_status)
          setFormData({
            hours_format: hours_format || '',
            clock_time_format: clock_time_format || ''
          });
          if (pay_period == 'bi_weekly') {
            const targetDate = res.data.effect_start_date;
            const result = getWeekDayOption(targetDate);
            setWeekStartDay(result)
          } else {
            setWeekStartDay(weekDay[parseInt(res.data.week_start_day) || 0])
            setStartDay(res.data.week_start_day)
          }
          setPeriod(pay_period || 'weekly')
          setPayPeriodDay1(payPeriodDay[parseInt(res.data.pay_period_start) - 1] || payPeriodDay[0])
          setPayPeriodDay2(payPeriodDay[parseInt(res.data.pay_period_start_2) - 1] || payPeriodDay[14])
          let effectDate = null;
          if (res.data.effect_start_date) {
            effectDate = new Date(res.data.effect_start_date);
          }
          dateAddForm.setValue('effectStartDate', effectDate || new Date());
          // dateAddForm.setValue('effectStartDate', res.data.effect_start_date)
          setReportTypes(reportTypes[parseInt(res.data.export_report) || 0])
          setAutoReportDelivery(res.data.auto_report_delivery)

          // 获取加班规则数据
          if (res.data.overtime_rule) {
            const overtimeRule = res.data.overtime_rule;
            setOvertimeEnabled(overtimeRule.enabled || false);
            setOvertimeType(overtimeRule.overtime_type || 'daily');
            setT1Value(overtimeRule.t1?.toString() || '20');
            setT2Value(overtimeRule.t2?.toString() || '40');
            setWeeklyAlertsEnabled(overtimeRule.weekly_alerts_enabled || false);
            setHoursRangeLowMin(overtimeRule.hours_range?.low?.min?.toString() || '0');
            setHoursRangeLowMax(overtimeRule.hours_range?.low?.max?.toString() || '40');
            setHoursRangeMediumMin(overtimeRule.hours_range?.medium?.min?.toString() || '40');
            setHoursRangeMediumMax(overtimeRule.hours_range?.medium?.max?.toString() || '80');
            setHoursRangeHighMin(overtimeRule.hours_range?.high?.min?.toString() || '80');
            setHoursRangeHighMax(overtimeRule.hours_range?.high?.max?.toString() || '');
            setOvertimeData(overtimeRule);
          }

          if (res.data.receiving_emails && Array.isArray(res.data.receiving_emails)) {
            dateAddForm.setValue('share', res.data.receiving_emails.join(';'))
          }
        }
      })
    };
    CompanyService.dateFormat({method: "get"}).then((res) => {
      const date_format = res.data?.date_format?.toString() || "MM/dd/yyyy";
      setDateFormat(date_format);
    });
    fetchSettings();

  }, []);

  const handleChange = (event: any) => {
    const {name, value} = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 加班规则处理函数
  const handleOvertimeToggle = () => {
    setOvertimeEnabled(!overtimeEnabled);
  };

  const handleOvertimeTypeChange = (value: 'daily' | 'weekly') => {
    setOvertimeType(value);
  };

  const handleT1Change = (value: string) => {
    setT1Value(value);
  };

  const handleT2Change = (value: string) => {
    setT2Value(value);
  };

  const incrementT1 = () => {
    const numT2 = parseFloat(t2Value) || 0;
    let newValue = ((parseFloat(t1Value) || 0) + 1);
    if (newValue > 80) {
      newValue = 80;
      enqueueSnackbar(t("common.attendance.T1 Weekly Overtime must be between 8 and 80 hours"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    if (newValue < 8) {
      newValue = 8;
      enqueueSnackbar(t("common.attendance.T1 Weekly Overtime must be between 8 and 80 hours"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    // T1 must be less than T2 (T1 <= T2 - 1)
    if (newValue >= numT2) {
      newValue = numT2 - 1;
      enqueueSnackbar(t("common.attendance.T1 must be less than T2"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    if (newValue < 8) {
      newValue = 8;
      enqueueSnackbar(t("common.attendance.T1 Weekly Overtime must be between 8 and 80 hours"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    setT1Value(newValue.toString());
  };

  const decrementT1 = () => {
    let newValue = ((parseFloat(t1Value) || 1) - 1);
    if (newValue > 80) {
      newValue = 80;
      enqueueSnackbar(t("common.attendance.T1 Weekly Overtime must be between 8 and 80 hours"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    if (newValue < 8) {
      newValue = 8;
      enqueueSnackbar(t("common.attendance.T1 Weekly Overtime must be between 8 and 80 hours"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    // T1 must be less than T2 (T1 <= T2 - 1)
    const numT2 = parseFloat(t2Value) || 0;
    if (newValue >= numT2) {
      newValue = numT2 - 1;
    }
    setT1Value(newValue.toString());
  };

  const incrementT2 = () => {
    const numT1 = parseFloat(t1Value) || 0;
    let newValue = ((parseFloat(t2Value) || 0) + 1);
    if (newValue > 80) {
      newValue = 80;
      enqueueSnackbar(t("common.attendance.T2 Weekly Overtime must be between 8 and 80 hours"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    if (newValue < 8) {
      newValue = 8;
      enqueueSnackbar(t("common.attendance.T2 Weekly Overtime must be between 8 and 80 hours"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    // T2 must be greater than T1 (T2 >= T1 + 1)
    if (newValue <= numT1) {
      newValue = numT1 + 1;
      enqueueSnackbar(t("common.attendance.T2 must be greater than T1"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    setT2Value(newValue.toString());
  };

  const decrementT2 = () => {
    const numT1 = parseFloat(t1Value) || 0;
    let newValue = ((parseFloat(t2Value) || 1) - 1);
    if (newValue > 80) {
      newValue = 80;
      enqueueSnackbar(t("common.attendance.T2 Weekly Overtime must be between 8 and 80 hours"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    if (newValue < 8) {
      newValue = 8;
      enqueueSnackbar(t("common.attendance.T2 Weekly Overtime must be between 8 and 80 hours"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    // T2 must be greater than T1 (T2 >= T1 + 1)
    if (newValue <= numT1) {
      newValue = numT1 + 1;
      enqueueSnackbar(t("common.attendance.T2 must be greater than T1"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    setT2Value(newValue.toString());
  };

  const handleWeeklyAlertsToggle = () => {
    setWeeklyAlertsEnabled(!weeklyAlertsEnabled);
  };

  // Hours Range 输入处理函数 - 保持边界值一致
  const handleHoursRangeLowMinChange = (value: string) => {
    setHoursRangeLowMin(value);
  };

  const handleHoursRangeLowMaxChange = (value: string) => {
    setHoursRangeLowMax(value);
    setHoursRangeMediumMin(value);
  };

  const handleHoursRangeMediumMinChange = (value: string) => {
    setHoursRangeMediumMin(value);
    setHoursRangeLowMax(value);
  };

  const handleHoursRangeMediumMaxChange = (value: string) => {
    // MediumMax 和 HighMin 必须保持一致
    setHoursRangeMediumMax(value);
    setHoursRangeHighMin(value);
  };

  const handleHoursRangeHighMinChange = (value: string) => {
    // HighMin 和 MediumMax 必须保持一致
    setHoursRangeHighMin(value);
    setHoursRangeMediumMax(value);
  };

  const handleHoursRangeHighMaxChange = (value: string) => {
    setHoursRangeHighMax(value);
  };

  const handleSubmit = async () => {
    if (period === 'semi_monthly') {
      if (!payPeriodDay1.name) {
        enqueueSnackbar(t("common.Pay Period 1 Start has no value."), {
          variant: "error",
          anchorOrigin: {vertical: "top", horizontal: "center"}
        });
        return;
      }
      if (!payPeriodDay2.name) {
        enqueueSnackbar(t("common.Pay Period 2 Start has no value."), {
          variant: "error",
          anchorOrigin: {vertical: "top", horizontal: "center"}
        });
        return;
      }
      const period1Num = parseInt(payPeriodDay1.key);
      const period2Num = parseInt(payPeriodDay2.key);
      if (period1Num >= period2Num) {
        enqueueSnackbar(t("common.Pay Period 1 must be less than Pay Period 2"), {
          variant: "error",
          anchorOrigin: {vertical: "top", horizontal: "center"}
        });
        return;
      }
    }

    const data = dateAddForm.getValues()
    let effect_start_date;
    if (typeof data.effectStartDate === 'string') {
      effect_start_date = data.effectStartDate;
    } else if (data.effectStartDate instanceof Date) {
      effect_start_date = Util.datetime2string(data.effectStartDate, "yyyy-MM-dd");
    }
    const share = data?.share
    if (reportType.key === "1" && autoReportDelivery) {
      if (! share) {
        SnackbarUtil.error(t('common.You must keep at least one valid email address.'), {
          anchorOrigin: {vertical: "top", horizontal: "center"}
        });
        return;
      }
    }
    dateAddForm.clearErrors("share");
    let isInvalidEmail = false;
    let share_email;
    if (share) {
      share_email = share.trim()
      .replace(/[\r\n\s]/g, "")
      .split(';')
      .filter((item: string) => {
        const email_pattern = /^\w[-\w.+]*@([A-Za-z0-9][-A-Za-z0-9]+\.)+[A-Za-z]{2,14}$/;
        if (item !== "" && email_pattern.test(item)) {
          return true;
        }
        isInvalidEmail = true
        return false;
      });
    }
    if (isInvalidEmail) {
      dateAddForm.setError("share", { type: "custom", message: t("common.Invalid email format") }, { shouldFocus: true })
      return
    }

    const payload = {
      hours_format: formData.hours_format,
      clock_time_format: formData.clock_time_format,
      effect_start_date: effect_start_date,
      pay_period: period,
      week_start_day: weekStartDay.key,
      pay_period_start: payPeriodDay1.key,
      pay_period_start_2: payPeriodDay2.key,
      export_report: reportType.key,
      auto_report_delivery: autoReportDelivery,
      receiving_emails: share_email,
      overtime_rule: {
        enabled: overtimeEnabled,
        overtime_type: overtimeType,
        t1: parseFloat(t1Value) || 0,
        t2: parseFloat(t2Value) || 0,
        weekly_alerts_enabled: weeklyAlertsEnabled,
        hours_range: {
          low: {
            min: parseFloat(hoursRangeLowMin) || 0,
            max: parseFloat(hoursRangeLowMax) || 0
          },
          medium: {
            min: parseFloat(hoursRangeMediumMin) || 0,
            max: parseFloat(hoursRangeMediumMax) || 0
          },
          high: {
            min: parseFloat(hoursRangeHighMin) || 0,
            max: hoursRangeHighMax ? parseFloat(hoursRangeHighMax) : null
          }
        }
      }
    };

    GenericService.create(ATTEndpoints.ATT_UPDATE_SETTING_URI, payload).then((res) => {
      if (res.data && res.status === 200) {
        enqueueSnackbar(t("common.success"), {
          variant: "success",
          anchorOrigin: {vertical: "top", horizontal: "center"}
        });
      }
    })
  }

  const PayPeriodComponent = () => {
    if (subscriptionFlag === false) {
      return (
        <Grid item xs={12}>
          <Box
            sx={{
              padding: 4,
              backgroundColor: '#FFFFFF',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '50%',
            }}
          >
            <Box sx={{mb: 3}}>
              <IconSubscriptionPhone/>
            </Box>
            <Typography
              variant="h5"
              sx={{
                color: '#2BA9DE',
                fontWeight: 'bold',
                mb: 2,
                textAlign: 'center',
                fontFamily: "Alibaba PuHuiTi 2.0",
                fontSize: "16px"
              }}
            >
              {t("common.Unlock Pay Period Settings")}
            </Typography>
            <Typography
              variant="body1"
              sx={{color: '#333333', mb: 3, textAlign: 'center', px: 5, fontSize: "14px", fontWeight: "normal"}}
            >
              {t("common.Configure payroll cycles (Weekly / Bi-Weekly / Semi-Monthly / Monthly) by subscribing in the NGTeco Office App.")}
            </Typography>
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#2BA9DE',
                '&:hover': {backgroundColor: '#2BA9DE'},
                padding: '10px 20px', fontSize: '14px', fontWeight: 'bold', textTransform: 'none', mb: 3,
              }}
              onClick={() => {
                SnackbarUtil.warning(t("common.Subscribe in App Tips"), {
                  anchorOrigin: {vertical: 'top', horizontal: 'center'}
                });
                return;
              }}
            >
              {t("common.Subscribe in App")}
            </Button>
            <Box sx={{width: '100%', mt: 1}}>
              <Box sx={{width: '100%', height: '1px', backgroundColor: '#E0E0E0', mb: 2}}/>
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5}}>
                <SubscriptionAppStore/>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#999999',
                    whiteSpace: 'nowrap',
                    fontFamily: "Alibaba PuHuiTi 2.0",
                    fontSize: "14px",
                    fontWeight: "normal",
                    lineHeight: "16px",
                  }}
                >
                  {t("common.Available on App Store & Google Play")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      );
    } else {
      // 为 semi_monthly 模式准备可选的 payPeriodDay 选项
      const getAvailablePayPeriodDaysForPeriod2 = () => {
        if (period === 'semi_monthly') {
          const period1Key = parseInt(payPeriodDay1.key);
          return payPeriodDay.filter(day => parseInt(day.key) >= period1Key);
        }
        return payPeriodDay;
      };

      const getAvailablePayPeriodDaysForPeriod1 = () => {
        if (period === 'semi_monthly') {
          const period2Key = parseInt(payPeriodDay2.key);
          return payPeriodDay.filter(day => parseInt(day.key) < period2Key);
        }
        return payPeriodDay;
      };

      // 处理 payPeriodDay1 变化
      const handlePayPeriodDay1Change = (newValue: DayOption | null) => {
        if (newValue) {
          setPayPeriodDay1(newValue);
          // 如果 period1 大于等于 period2，自动调整 period2 为 period1 的下一个有效值
          if (period === 'semi_monthly') {
            const period1Num = parseInt(newValue.key);
            const period2Num = parseInt(payPeriodDay2.key);
            if (period1Num >= period2Num) {
              const nextValidDay = payPeriodDay.find(day => parseInt(day.key) > period1Num);
              if (nextValidDay) {
                setPayPeriodDay2({key: '32', name: ''});
              } else {
                // 如果没有更大的选项，提示用户
                SnackbarUtil.warning(t("common.Pay Period 1 cannot be greater than or equal to Pay Period 2"), {
                  anchorOrigin: {vertical: 'top', horizontal: 'center'}
                });
              }
            }
          }
        }
      };

      // 处理 payPeriodDay2 变化
      const handlePayPeriodDay2Change = (newValue: DayOption | null) => {
        if (newValue) {
          setPayPeriodDay2(newValue);
          const period1Num = parseInt(payPeriodDay1.key);
          const period2Num = parseInt(newValue.key);
          if (period === 'semi_monthly' && period1Num >= period2Num) {
            SnackbarUtil.warning(t("common.Pay Period 2 must be greater than Pay Period 1"), {
              anchorOrigin: {vertical: 'top', horizontal: 'center'}
            });
            return;
          }
        }
      };

      return (
        <Grid item xs={12}>
          <Box>
            <Box sx={{backgroundColor: '#ffffff', borderRadius: '8px'}}>
              <Typography
                sx={{
                  textAlign: 'left',
                  color: "#333333",
                  fontSize: "16px",
                  fontWeight: "bold"
                }}>{t("common.Pay Period")}</Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: '#ffffff', borderRadius: '8px', color: "#3D3D3D", fontSize: "14px",
                flexDirection: "column",
                marginTop: 3
              }}>
              <RadioGroup row={true} value={period} onChange={(event, newValue) => {
                setPeriod(newValue)
                if (newValue == 'bi_weekly') {
                  const targetDate = dateAddForm.getValues('effectStartDate').toString() || new Date().toString();
                  const result = getWeekDayOption(targetDate);
                  setWeekStartDay(result)
                } else {
                  setWeekStartDay(weekDay[parseInt(startDay) || 0])
                }
              }}>
                <FormControlLabel value={"weekly"} control={<Radio/>} label={t("common.Weekly")}/>
                <FormControlLabel value={"bi_weekly"} control={<Radio/>} label={t("common.Bi-Weekly")}/>
                <FormControlLabel value={"semi_monthly"} control={<Radio/>} label={t("common.Semi-Monthly")}/>
                <FormControlLabel value={"monthly"} control={<Radio/>} label={t("common.Monthly")}/>
              </RadioGroup>
            </Box>

            {period === 'bi_weekly' && (
              <Box sx={{backgroundColor: '#ffffff', borderRadius: '8px', marginTop: 3}}>
                <NGInputLabel
                  sx={{
                    color: "#666666",
                    fontSize: "16px",
                    fontWeight: "normal",
                    fontFamily: "Alibaba PuHuiTi 2.0",
                    lineHeight: "16px"
                  }}
                  label={t("common.Effect Start Date")}/>
                <Grid sx={{paddingTop: '10px'}}>
                  <Controller
                    control={dateAddForm.control}
                    name="effectStartDate"
                    render={({field, fieldState}) =>
                      <DatePicker
                        disableFuture
                        inputFormat={dateFormat}
                        value={field.value || null}
                        onChange={(newValue) => {
                          field.onChange(newValue)
                          if (period == 'bi_weekly') {
                            const targetDate = newValue?.toString() || new Date().toString();
                            const result = getWeekDayOption(targetDate);
                            setWeekStartDay(result)
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            sx={{
                              width: '20vw', '& .MuiInputBase-input': {
                                fontSize: "14px"
                              }
                            }}
                            {...params}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    }
                  />
                </Grid>
              </Box>
            )}

            {['semi_monthly', 'monthly'].includes(period) && (
              <Box sx={{backgroundColor: '#ffffff', borderRadius: '8px', marginTop: 3}}>
                <NGInputLabel sx={{color: "#666666", fontSize: "16px", fontWeight: "normal"}}
                              label={t("common.Pay Period 1 Start")} required={true}/>
                <Autocomplete
                  sx={{marginTop: 1}}
                  fullWidth
                  options={payPeriodDay}
                  value={payPeriodDay1}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.key === value.key}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth={false} size={"small"} sx={{
                      width: "20vw", '& .MuiInputBase-input': {
                        fontSize: "14px"
                      },
                    }}/>
                  )}
                  onChange={(event, newValue) => handlePayPeriodDay1Change(newValue)}
                />
              </Box>
            )}

            {['semi_monthly'].includes(period) && (
              <Box sx={{backgroundColor: '#ffffff', borderRadius: '8px', marginTop: 3}}>
                <NGInputLabel sx={{color: "#666666", fontSize: "16px", fontWeight: "normal"}}
                              label={t("common.Pay Period 2 Start")} required={true}/>
                <Autocomplete
                  sx={{marginTop: 1}}
                  fullWidth
                  options={getAvailablePayPeriodDaysForPeriod2()}
                  value={payPeriodDay2}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.key === value.key}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth={false} size={"small"} sx={{
                      width: "20vw", '& .MuiInputBase-input': {
                        fontSize: "14px"
                      },
                    }}/>
                  )}
                  onChange={(event, newValue) => handlePayPeriodDay2Change(newValue)}
                />
              </Box>
            )}

            <Box sx={{backgroundColor: '#ffffff', borderRadius: '8px', marginTop: 3}}>
              <NGInputLabel sx={{color: "#666666", fontSize: "16px", fontWeight: "normal"}}
                            label={t("common.Start Day of the week")} required={true}/>
              <Autocomplete
                sx={{marginTop: 1}}
                fullWidth
                options={weekDay}
                value={weekStartDay}
                disabled={period === 'bi_weekly'}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.key === value.key}
                renderInput={(params) => (
                  <TextField {...params} fullWidth={false} size={"small"} sx={{
                    width: "20vw", '& .MuiInputBase-input': {
                      fontSize: "14px"
                    },
                  }}/>
                )}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setWeekStartDay(newValue);
                    setStartDay(newValue.key)
                  }
                }}
              />
            </Box>
          </Box>
        </Grid>
      );
    }
  };

  const ReportSettingComponent = () => {
    return (
      <Grid item xs={12}>
        <Box sx={{backgroundColor: '#ffffff', borderRadius: '8px', marginTop: 3}}>
          <Typography sx={{fontWeight: 'bold', marginBottom: '32px', fontSize: "16px"}}>
            {t("common.Report Setting")}
          </Typography>
          <Typography sx={{fontSize: '14px', color: '#666', marginBottom: '8px', fontWeight: 'normal'}}>
            {t("common.Select Report")}<span style={{color: 'red'}}>*</span>
          </Typography>
          <Select
            size="small"
            disabled={!subscriptionFlag}
            value={reportType.key}
            onChange={(event) => {
              const selectedKey = event.target.value;
              const selectedItem = reportTypes.find(item => item.key === selectedKey);
              if (selectedItem) setReportTypes(selectedItem);
            }}
            sx={{width: '20vw', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: "14px"}}
          >
            {reportTypes.map((item) => (
              <MenuItem key={item.key} value={item.key}>{item.name}</MenuItem>
            ))}
          </Select>
        </Box>

        {reportType.key === "1" && (
          <Grid item xs={6} id="email-section">
            <Box sx={{
              bgcolor: '#F8F9FA',
              borderRadius: '12px',
              p: 3,
              mt: 3,
              border: '1px solid #E8EDF2'
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '24px',
                mb: 2
              }}>
                <Typography sx={{fontWeight: 'bold', color: '#333333', fontSize: "16px"}}>
                  {t("common.Auto Report Delivery")}
                </Typography>
                <Switch
                  checked={autoReportDelivery}
                  onChange={(e) => {
                    setAutoReportDelivery(e.target.checked);
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#2BA9DE',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#2BA9DE',
                    },
                  }}
                />
              </Box>

              <Typography variant="body2" sx={{color: '#333333', mb: 3, fontSize: "14px", fontWeight: "normal"}}>
                {t("common.The report will be emailed to you the day after the pay period closes.")}
              </Typography>

              {autoReportDelivery && (
                <Box sx={{width: '100%'}}>
                  <Typography sx={{fontSize: '14px', fontWeight: 'normal', color: '#666666', mb: 1}}>
                    {t("common.Receiving Email")}
                  </Typography>

                  <ControlledTextFiled
                    hookForm={dateAddForm}
                    sx={{width: '100%', minWidth: '600px'}}
                    name="share"
                    multiline
                    minRows={3}
                    maxRows={5}
                    type="text"
                  />
                  <Box padding={"8px 0"}>
                    <Typography sx={{color: "#999999", fontSize: '12px',}}>{t("export.Email List")}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    )
  }

  return (
    <Grid
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "0 24px 100px 24px",
        width: "calc(100% - 24px)",
        backgroundColor: "#ffffff",
        border: '1px solid #E0E0E0',
        minHeight: "calc(100vh - 94px)",
        marginBottom: '24px',
        position: "relative"
      }}>
      <CustomBreadcrumbs record={[{label: t("common.Attendance Setting")}]}/>

      {/* TAB 标签页 - 预留后续扩展 */}
      <Box sx={{borderBottom: 1, borderColor: 'divider', mt: 2}}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="attendance settings tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 'bold',
              minWidth: '120px',
            },
            '& .Mui-selected': {
              color: '#2BA9DE',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#2BA9DE',
            }
          }}
        >
          <Tab label={t("common.Salary & Report Format")} {...a11yProps(0)} />
          <Tab label={t("common.Overtime Rule")} {...a11yProps(1)} />
          {/* 后续可添加更多 Tab，例如：
            <Tab label={t("Notification Settings")} {...a11yProps(2)} />
          */}
        </Tabs>
      </Box>

      <Grid container direction="column" alignItems="stretch" style={{width: "100%", flex: "1 0 auto"}}>
        {/* 基础设置 Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* Pay Period 组件 */}
          <PayPeriodComponent/>

          {/* Report Hours Format */}
          <Grid item xs={12}>
            <Box>
              <Box sx={{backgroundColor: '#ffffff', borderRadius: '8px', marginTop: 5}}>
                <Typography sx={{
                  textAlign: 'left',
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#333333"
                }}>{t("common.Report Hours Format")}</Typography>
              </Box>
              <Box sx={{backgroundColor: '#ffffff', borderRadius: '8px', marginTop: 3}}>
                <NGInputLabel sx={{fontSize: "16px", fontWeight: "normal"}} label={t("common.Duration Format")}
                              required={true}/>
                <FormControl component="fieldset" sx={{width: '100%'}}>
                  <RadioGroup row name="hours_format" value={formData.hours_format} onChange={handleChange}>
                    <FormControlLabel sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: "14px",
                        fontWeight: "normal",
                        color: "#3D3D3D"
                      }
                    }} value="Decimal" control={<Radio sx={{color: '#666666'}}/>} label={t("common.Decimal")}/>
                    <FormControlLabel sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: "14px",
                        fontWeight: "normal",
                        color: "#3D3D3D"
                      }
                    }} value="HH:mm" control={<Radio sx={{color: '#666666'}}/>} label="HH:MM"/>
                  </RadioGroup>
                </FormControl>
              </Box>
              <Box sx={{backgroundColor: '#ffffff', borderRadius: '8px', marginTop: 2}}>
                <NGInputLabel sx={{fontSize: "16px", fontWeight: "normal"}} label={t("common.Clock Time Format")}
                              required={true}/>
                <FormControl component="fieldset" sx={{width: '100%'}}>
                  <RadioGroup row name="clock_time_format" value={formData.clock_time_format} onChange={handleChange}>
                    <FormControlLabel sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: "14px",
                        fontWeight: "normal",
                        color: "#3D3D3D"
                      }
                    }} value="HH:mm" control={<Radio sx={{color: '#666666'}}/>} label="HH:MM"/>
                    <FormControlLabel sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: "14px",
                        fontWeight: "normal",
                        color: "#3D3D3D"
                      }
                    }} value="hh:mm" control={<Radio sx={{color: '#666666'}}/>} label="hh:mm AM/PM"/>
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>
          </Grid>

          {/* Report Setting 组件 */}
          <ReportSettingComponent/>
        </TabPanel>

        {/* 加班规则 TabPanel */}
        <TabPanel value={tabValue} index={1}>
          <AttendanceOvertimeRule
            overtimeEnabled={overtimeEnabled}
            overimeType={overtimeType}
            t1Value={t1Value}
            t2Value={t2Value}
            weeklyAlertsEnabled={weeklyAlertsEnabled}
            hoursRangeLowMin={hoursRangeLowMin}
            hoursRangeLowMax={hoursRangeLowMax}
            hoursRangeMediumMin={hoursRangeMediumMin}
            hoursRangeMediumMax={hoursRangeMediumMax}
            hoursRangeHighMin={hoursRangeHighMin}
            hoursRangeHighMax={hoursRangeHighMax}
            subscriptionFlag={subscriptionFlag}
            onOvertimeToggle={handleOvertimeToggle}
            onOvertimeTypeChange={handleOvertimeTypeChange}
            onT1Change={handleT1Change}
            onT2Change={handleT2Change}
            onIncrementT1={incrementT1}
            onDecrementT1={decrementT1}
            onIncrementT2={incrementT2}
            onDecrementT2={decrementT2}
            onWeeklyAlertsToggle={handleWeeklyAlertsToggle}
            onHoursRangeLowMinChange={handleHoursRangeLowMinChange}
            onHoursRangeLowMaxChange={handleHoursRangeLowMaxChange}
            onHoursRangeMediumMinChange={handleHoursRangeMediumMinChange}
            onHoursRangeMediumMaxChange={handleHoursRangeMediumMaxChange}
            onHoursRangeHighMinChange={handleHoursRangeHighMinChange}
            onHoursRangeHighMaxChange={handleHoursRangeHighMaxChange}
          />
        </TabPanel>

        {/* 后续可添加更多 TabPanel，例如：
        <TabPanel value={tabValue} index={2}>
          <NotificationSettingsComponent />
        </TabPanel>
        */}
      </Grid>

      {/* 保存按钮 - 固定在白色页面左下角 */}
      <Box sx={{
        position: 'absolute',
        left: '24px',
        bottom: '24px',
        display: 'flex',
        justifyContent: 'flex-start',
        backgroundColor: '#ffffff',
        width: "fit-content",
        zIndex: 10
      }}>
        <SaveButton
          fullWidth
          onClick={handleSubmit}
          disableElevation
          sx={{textTransform: "capitalize", width: '160px', marginTop: '12px', marginLeft: '24px'}}
        />
      </Box>
    </Grid>
  )
}

export default AttendanceManagement;
